<template>
  <Dialog :open="props.showOtpScreen" @update:open="(val) => emit('update:showOtpScreen', val)">
    <DialogContent
      :disableOutsidePointerEvents="true"
      :disableEscapeKeyDown="true"
      :show-close-button="false"
      @escape-key-down="(e) => e.preventDefault()"
      @pointer-down-outside="(e) => e.preventDefault()"
      class="sm:max-w-[425px]"
    >
      <ErrorDisplay v-if="displayErrorMessage" :errorMessage="otpErrorMessage" />
      <div v-else>
        <DialogHeader>
          <DialogTitle>Verify Mobile</DialogTitle>
          <DialogDescription>
            <div class="flex flex-col gap-2">
              <span>A verification code has been sent to {{ user.cellNumber }}.</span>
              <span v-if="otpErrorMessage !== ''" class="text-error-700">
                {{ otpErrorMessage }}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div v-if="!isLoadingOtp">
          <OtpInput v-model="digitInputs" />
        </div>
        <LayupSpinner v-if="isLoadingOtp" />

        <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />
        <DialogFooter>
          <Button type="button" :disabled="isLoadingOtp || otpValue.length !== 5" @click="onHandleOtp"> Submit </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { forgotPassword, otpVerification } from '@/services/auth';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref, watch } from 'vue';
import LayupSpinner from '../common/LayupSpinner.vue';
import ErrorDisplay from './ErrorDisplay.vue';
import OtpInput from './OtpInput.vue';

const props = defineProps<{
  showOtpScreen: boolean;
}>();

const emit = defineEmits(['update:showOtpScreen']);

const userStore = useUserStore();
const { user } = userStore;
const isLoadingOtp = ref(true);
const otpErrorMessage = ref('');
const otpValue = ref('');
const digitInputs = ref(['', '', '', '', '']);
const turnstileToken = ref('');
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const displayErrorMessage = !userStore.user.cellNumber;

watch(
  () => props.showOtpScreen,
  (isOpen) => {
    if (isOpen && displayErrorMessage) {
      otpErrorMessage.value = 'Your session has expired. Please go back and try again.';
      createWarning(new Error(otpErrorMessage.value));
    }
  },
);

watch(digitInputs, async (newOtp) => {
  otpValue.value = newOtp.join('');
});

watch(otpValue, async (newOtp) => {
  if (newOtp.length === 5) {
    await onHandleOtp();
  }
});

const sendOtp = async () => {
  try {
    otpErrorMessage.value = '';
    isLoadingOtp.value = true;
    await forgotPassword('', user.cellNumber!, 'MOBILE', turnstileToken.value);
    addMetric(`resend_otp`, { status: 'success', channel: 'merchant' });
    turnstileRef.value?.reset();
    turnstileToken.value = '';
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`resend_otp`, { status: 'network_error', channel: 'merchant' });
      otpErrorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric(`resend_otp`, { status: 'error', channel: 'merchant' });
      if (error?.response?.data) {
        otpErrorMessage.value = error.response.data;
      } else {
        otpErrorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    isLoadingOtp.value = false;
  }
};

const onHandleOtp = async () => {
  try {
    isLoadingOtp.value = true;
    otpErrorMessage.value = '';
    const userResponse = await otpVerification(otpValue.value, 'MOBILE', user.cellNumber!);
    userStore.user = userResponse;

    emit('update:showOtpScreen', false);
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`otp_verification`, { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric(`otp_verification`, { status: 'error', channel: 'merchant' });
      if (error?.response?.data) {
        otpErrorMessage.value = error.response.data;
      } else {
        otpErrorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    isLoadingOtp.value = false;
  }
};

const handleSuccess = async (token: string) => {
  turnstileToken.value = token;
  await sendOtp();
};
</script>
